/**
 * Submission-time BAC PostHog context. Never create an identifier here.
 * This is not an authentication credential or acquisition storage.
 */
export type BookingAnalyticsContext = {
  version: 1;
  consent: "granted" | "denied" | "pending";
  distinctId?: string;
  unavailableReason?: "numeric" | "malformed";
};

export type BookingIdentityStatus =
  | "ready" | "missing" | "denied" | "pending" | "unavailable" | "malformed" | "numeric";

export type SavedBookingAnalyticsContext = {
  version: 1;
  status: BookingIdentityStatus;
  distinctId?: string;
};

export const BOOKING_ANALYTICS_SNAPSHOT_KEY = "_bac_booking_analytics";

export function validBookingDistinctId(value: unknown): value is string {
  return typeof value === "string"
    && value.length > 0 && value.length <= 200
    && /^[A-Za-z0-9._:-]+$/.test(value)
    && !/^[0-9]+$/.test(value);
}

/** Strip denied/malformed input rather than rejecting the booking. */
export function normalizeBookingAnalyticsContext(raw: unknown): SavedBookingAnalyticsContext {
  if (raw == null) return { version: 1, status: "missing" };
  if (typeof raw !== "object" || Array.isArray(raw)) return { version: 1, status: "malformed" };
  const c = raw as Record<string, unknown>;
  if (c.version !== 1) return { version: 1, status: "malformed" };
  if (c.consent === "denied" || c.consent === "pending") return { version: 1, status: c.consent };
  if (c.consent !== "granted") return { version: 1, status: "malformed" };
  if (c.distinctId == null) return {
    version: 1,
    status: c.unavailableReason === "numeric" || c.unavailableReason === "malformed"
      ? c.unavailableReason : "unavailable",
  };
  if (typeof c.distinctId === "string" && /^[0-9]+$/.test(c.distinctId)) {
    return { version: 1, status: "numeric" };
  }
  return validBookingDistinctId(c.distinctId)
    ? { version: 1, status: "ready", distinctId: c.distinctId }
    : { version: 1, status: "malformed" };
}

export function bookingAnalyticsContextFromSdkId(id: unknown): BookingAnalyticsContext {
  return {
    version: 1, consent: "granted",
    ...(validBookingDistinctId(id)
      ? { distinctId: id }
      : id == null ? {} : {
          unavailableReason: typeof id === "string" && /^[0-9]+$/.test(id)
            ? "numeric" as const : "malformed" as const,
        }),
  };
}

/** Saved data is validated again at delivery; older sessions are explicit misses. */
export function readSavedBookingAnalyticsContext(raw: unknown): SavedBookingAnalyticsContext {
  if (raw == null) return { version: 1, status: "missing" };
  if (typeof raw !== "object" || Array.isArray(raw)) return { version: 1, status: "malformed" };
  const c = raw as Record<string, unknown>;
  if (c.version !== 1) return { version: 1, status: "malformed" };
  if (c.status === "ready") {
    return normalizeBookingAnalyticsContext({ version: 1, consent: "granted", distinctId: c.distinctId });
  }
  if (["missing", "denied", "pending", "unavailable", "malformed", "numeric"].includes(String(c.status))) {
    return { version: 1, status: c.status as BookingIdentityStatus };
  }
  return { version: 1, status: "malformed" };
}
